<template>
	<div>
		<v-select
				v-bind="$attrs"
				ref="bwSearchSelect"
				class="bw-search-select rounded-lg"
				deletable-chips
				:items="sortedItems"
				:menu-props="{ closeOnContentClick: false }"
				outlined
				:rules="rules"
				small-chips
				v-on="$listeners"
				v-click-outside="{ handler: outsideClick, include: getActiveMenuContent }"
				:value="value"
		>
			<!-- Search & Select All/None -->
			<template v-slot:prepend-item>
				<!-- Search Field -->
				<v-text-field
						v-model="search"
						ref="searchField"
						class="mt-n2 search-input"
						hide-details
						placeholder="Search"
						single-line
						tabindex="-1"
						@input="filterItems"
				></v-text-field>

				<!-- Select All/None -->
				<template v-if="shouldShowSelectAll">
					<div
							class="align-center d-flex px-4 py-3"
							tabindex="0"
							@mousedown.prevent
							@click="toggleSelectAll"
					>
						<div class="pe-4">
							<v-icon :color="selectAllIconColor">
								{{ selectAllIcon }}
							</v-icon>
						</div>
						<div class="flex-grow-1">
							{{ selectAllText }}
						</div>
					</div>
				</template>
			</template>

			<!-- Custom Item Display w/Search Results -->
			<template v-slot:item="{ active, item, attrs, on }">
				<v-list-item
						v-on="on"
						v-bind="attrs"
						#default="{ active }"
						:ref="`listItems${getItemIndex(item)}`"
						:class="{ 'hide-option': filteredCount && getItemIndex(item) >= filteredCount }"
				>
					<v-list-item-action>
						<v-checkbox :input-value="active"></v-checkbox>
					</v-list-item-action>
					<v-list-item-content>

						<slot name="item-label" :item="item">
							<!-- Default content if no slot is provided -->
							<v-list-item-title>
								{{ getItemText(item) }}
							</v-list-item-title>
						</slot>

					</v-list-item-content>
				</v-list-item>
			</template>

			<!-- Custom Chip Display -->
			<template v-slot:selection="{ item, index }">
				<v-chip
						:key="JSON.stringify(item)"
						class="chip--select-multi"
						close
						:data-chip-index="index"
						:data-chip-id="item[itemValueKey]"
						small
						@click:close="removeChip(item)"
						v-intersect="onChipIntersect"
				>
					{{ item[itemTextKey] }}
				</v-chip>
			</template>

			<!-- Overflow Count -->
			<template v-if="isMultiple" v-slot:append>
				<div class="d-flex justify-end" style="min-width: 75px;">
					<div
							v-if="overflowItemsCount > 0"
							:class="[
								'overflow-count',
								'mt-3 mx-2 px-2',
								'rounded-pill',
								'font-weight-bold secondary--text',
								'text-caption text--darken-2 text-no-wrap',
								'cursor-pointer'
							]"
					>
						{{ `+${overflowItemsCount}` }}
					</div>
					<div v-else style="height: 30px;"></div>
					<div class="pb-1">
						<v-icon v-if="!$refs.bwSearchSelect || !$refs.bwSearchSelect.isMenuActive">
							mdi-menu-down
						</v-icon>
						<v-icon v-else>mdi-menu-up</v-icon>
					</div>
				</div>
			</template>
		</v-select>

		<v-dialog
				v-if="dialog.showConfirmRemove"
				content-class="rounded-xl"
				persistent
				scrollable
				:value="true"
				:width="600"
		>
			<v-card class="position-relative" rounded="xl">
				<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
					<div class="w-100">
						<div class="align-center d-flex justify-space-between">
							<h6>Remove Category</h6>
						</div>
					</div>
				</v-card-title>
				<v-card-text class="text-body-2">
					{{ removeMessage }}
				</v-card-text>
				<v-card-actions>
					<div class="align-center d-flex justify-space-between w-100">
						<div></div>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									outlined
									@click="cancelRemoveChip"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									@click="confirmRemoveChip(removingItem)"
							>
								Confirm
							</v-btn>
						</div>
					</div>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { helpers } from "../helpers";

export default {
	inheritAttrs: false,
	name: 'BwSearchSelect',
	props: {
		confirmRemove: Boolean,
		confirmRemoveMessage: String,
		customSearch: {
			type: Function,
			required: false,
		},
		hideSelectAll: Boolean,
		items: {
			type: Array,
			default: () => []
		},
		rules: {
			type: Array,
			default: () => []
		},
		sortAlpha: Boolean,
		value: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			actualSelectedItems: [],
			chipVisibility: [],
			dialog: {
				showConfirmRemove: false
			},
			filteredCount: 0,
			filteredItems: [],
			removalConfirmed: false,
			removingItem: null,
			search: '',
			searchResults: []
		};
	},
	mounted() {
		this.filteredItems = [...(this.items || [])];
	},
	computed: {
		isDense() {
			return this.$attrs.hasOwnProperty('dense');
		},

		isMultiple() {
			return this.$attrs.hasOwnProperty('multiple');
		},

		isStringArray() {
			return typeof this.items[0] !== 'object';
		},

		itemTextKey() {
			// Just like the v-select internal
			return this.$attrs['item-text'] || 'text';
		},

		itemValueKey() {
			// Just like the v-select internal
			return this.$attrs['item-value'] || 'value';
		},

		overflowItemsCount() {
			// Only return a calculated value if the multiple prop was passed
			if(this.isMultiple) {
				const lastVisibleIndex = this.chipVisibility.lastIndexOf(true);
				if(lastVisibleIndex < 0 || this.value.length < 2) return 0;
				return this.value.length - lastVisibleIndex - 1;
			}
			// Otherwise return 0
			return 0;
		},

		removeMessage() {
			//return false;
			if(!this.confirmRemoveMessage)
				return `Are you sure you want to remove (${this.getItemText(this.removingItem)})?`;
			else
				return this.confirmRemoveMessage;
		},

		returnObject() {
			return this.$attrs.hasOwnProperty('return-object')
		},
		
		searchFunction() {
			return  this.customSearch || ((search, item) =>
					item[this.itemTextKey]?.toLowerCase().includes(search?.toLowerCase())
			);
		},

		selectAllIcon() {
			if(this.isMultiple) {
				const itemValueKey = this.itemValueKey; // Assuming this is the key for item values
				const compareValue = item => this.returnObject ? item : item[itemValueKey];

				const compareItems = this.searchResults.length
						? this.searchResults
						: this.items;

				const allSelected = this.value && compareItems.every(item =>
						this.value.includes(compareValue(item))
				);
				const someSelected = this.value && compareItems.some(item =>
						this.value.includes(compareValue(item))
				);

				if(allSelected) return "mdi-close-box";
				if(someSelected) return "mdi-minus-box";
				return "mdi-checkbox-blank-outline";
			}
			return null;
		},

		selectAllIconColor() {
			if(this.isMultiple) {
				const itemValueKey = this.itemValueKey;
				const compareValue = item => this.returnObject ? item : item[itemValueKey];

				if(this.searchResults.length) {
					return this.value && this.searchResults.every(item =>
							this.value.includes(compareValue(item))
					) ? 'green' : '';
				}
				else {
					return this.value && this.value.length === this.items.length
							? 'green'
							: '';
				}
			}
			return null;
		},

		selectAllText() {
			if(this.isMultiple) {
				const itemValueKey = this.itemValueKey;
				const compareValue = item => this.returnObject ? item : item[itemValueKey];

				if(this.searchResults.length) {
					return this.value && this.searchResults.every(item =>
							this.value.includes(compareValue(item))
					) ? 'Deselect All' : 'Select All';
				}
				else {
					return this.value && this.value.length === this.items.length
							? 'Deselect All'
							: 'Select All';
				}
			}
			return null;
		},

		shouldShowSelectAll() {
			return !this.hideSelectAll && this.isMultiple;
		},

		sortedItems() {
			if(this.sortAlpha) {
				// If sortAlpha is true, sort items alphabetically
				return [...this.filteredItems].sort((a, b) => {
					const textA = a[this.itemTextKey].toLowerCase();
					const textB = b[this.itemTextKey].toLowerCase();
					return textA.localeCompare(textB);
				});
			}
			else {
				// Otherwise return the items as they are
				return [...this.filteredItems];
			}
		}

	},
	methods: {
		cancelRemoveChip() {
			this.removalConfirmed = false;
			this.removingItem = null;
			this.dialog.showConfirmRemove = false;
		},

		confirmRemoveChip(item) {
			this.removalConfirmed = true;
			this.$nextTick(() => {
				this.removeChip(item);
			});
		},

		filterItems() {
			if(this.search) {
				this.searchResults = this.items.filter(item => this.searchFunction(this.search, item));
				const remainingItems = this.items.filter(item =>
						!this.searchResults.includes(item)	
				);
				this.filteredCount = this.searchResults.length;
				this.filteredItems = [...this.searchResults, ...remainingItems];
				this.filteredItems = [...this.searchResults];
			}
			else {
				this.filteredCount = 0;
				this.filteredItems = this.items;
			}
		},

		getActiveMenuContent() {
			return [...document.querySelectorAll('.v-menu__content.menuable__content__active')];
		},

		getItemIndex(item) {
			return this.filteredItems.indexOf(item);
		},

		getItemText(item) {
			if(typeof this.itemTextKey === 'function') {
				return this.itemTextKey(item);
			}
			return item[this.itemTextKey];
		},

		onChipIntersect(entry) {
			if(this.isMultiple) {
				const lastEntryIndex = entry.length - 1;
				const chipIdx = entry[lastEntryIndex].target.getAttribute('data-chip-index');
				const isVisible = entry[lastEntryIndex].intersectionRatio === 1;

				this.$set(this.chipVisibility, chipIdx, isVisible);
			}
		},

		outsideClick() {
			this.search = '';
			this.filterItems();
			this.searchResults = [];
		},

		removeChip(item) {
			// This should only be used with multiple
			if(this.isMultiple) {
				if(this.confirmRemove && !this.removalConfirmed) {
					this.removingItem = item;
					this.$nextTick(() => {
						this.dialog.showConfirmRemove = true;
					});
				}
				else {
					let newValues = [...this.value];

					// !!! REMOVE FROM VALUE AND EMIT HERE
					// Filter out the item
					this.$emit('input', newValues.filter(val => this.returnObject
							? val !== item
							: val !== item[this.itemValueKey])
					);

					this.removalConfirmed = false;
					this.removingItem = null;
					this.dialog.showConfirmRemove = false;

					// Always subtract, if the next item comes into view the observer will run and
					// adjust accordingly
					this.lastVisibleIndex -= 1;
				}
			}
		},

		toggleSelectAll() {
			let newValues;

			if(this.searchResults.length) {
				if(this.searchResults.every(item => this.value.includes(this.returnObject
						? item
						: item[this.itemValueKey]))) {
					newValues = this.value.filter(localItem =>
							!this.searchResults.some(searchItem =>
									this.returnObject
											? localItem === searchItem
											: localItem === searchItem[this.itemValueKey]
							)
					);
				}
				else {
					newValues = [
						...new Set([
							...this.value,
							...this.searchResults.map(item => this.returnObject
									? item
									: item[this.itemValueKey])
						])
					];
				}
			}
			else {
				if(this.value.length === this.items.length) {
					newValues = [];
				}
				else {
					newValues = this.returnObject
							? this.items.slice()
							: this.items.map(item => item[this.itemValueKey]);
				}
			}

			this.$emit('input', newValues);
		}
	},
	watch: {
		items(newItems) {
			this.filterItems();
		}
	}
};
</script>

<style scoped>
.v-input--dense {
	& .v-chip.chip--select-multi {
		margin: 4px !important;
	}
}

::v-deep .v-select__selections {
	max-height: 50px !important; /* Adjust based on how many chips you want to show at once */
	overflow: hidden !important;
}


::v-deep .v-select__selections.v-select__selections > input {
	left: 0;
	max-height: 32px !important;
	max-width: 0px !important;
	position: absolute;
	top: 0;
}

::v-deep .search-input input {
	padding: 0.25rem 1rem 1rem;
}

::v-deep .overflow-count {
	background: rgba(73, 178, 228, 0.08);
	border: solid 1px rgba(73, 178, 228, 0.12);
}

::v-deep .hide-option {
	display: none;
}

::v-deep .search-results {
	&--start {
	}

	&--end {
	}
}
</style>
